import { __assign } from "tslib";
import { fillTenantTokens, setTenantTokens } from './TenantTokenManager';
import { validateTelemetryEvent } from './TelemetryEventValidator';
import { logNotification, logError } from './OTelNotifications';
import { oteljsVersion } from './Version';
import { makeStringDataField, makeInt64DataField } from './DataFieldHelper';
var CUSTOMER_CONTENT_VERSION = 2;
var MAX_EVENT_QUEUE_LENGTH = 1000;
/**
 * Use this as the Nexus tenant token when Nexus logging is not needed or relevant
 */
export var SuppressNexus = -1;
/**
 * Clone a telemetry event; generally so we don't modify the input telemetry event
 * Also initializes telemetryProperties and dataFields if they aren't already set
 * @param telemetryEvent - The event to clone
 */
export function cloneEvent(telemetryEvent) {
    var localEvent = {
        eventName: telemetryEvent.eventName,
        eventFlags: telemetryEvent.eventFlags || {},
        telemetryProperties: !!telemetryEvent.telemetryProperties ? __assign({}, telemetryEvent.telemetryProperties) : {},
        dataFields: !!telemetryEvent.dataFields ? telemetryEvent.dataFields.slice() : [],
        timestamp: telemetryEvent.timestamp || new Date().getTime()
    };
    if (!!telemetryEvent.eventContract) {
        localEvent.eventContract = { name: telemetryEvent.eventContract.name, dataFields: telemetryEvent.eventContract.dataFields.slice() };
    }
    return localEvent;
}
/**
 * SimpleTelemetryLogger is the base implementation of a telemetry logger.
 * It exposes sendTelemetryEvent, which is used to log a telemetry event.
 *
 * Add a sink by calling addSink() or modifying the telemetrySinks list.
 * If no sinks are attached, it keeps a queue of events, but starts silently
 */
var SimpleTelemetryLogger = /** @class */ (function () {
    /**
     * Creates a telemetry logger
     * @param parent - A parent telemetry logger that this telemetry logger should inherit from
     *                 This telemetry logger will send telemetry to the same sinks
     *                 and will send the same persistent data fields as the parent logger
     * @param persistentDataFields - Data fields to send with every event
     */
    function SimpleTelemetryLogger(parent, persistentDataFields, config) {
        var _a, _b, _c;
        /**
         * A list of sinks that telemetry events will be forwarded to
         */
        this.telemetrySinks = [];
        /**
         * @internal Internal variable; do not use publicly.
         * 'Data.' will be prepended to each field.
         */
        this.persistentDataFields = [];
        /**
         * A list of Part A data fields that should be added to every event. In the 1DS Sink,
         * part A data fields will be sent without 'Data.'. On native clients, these fields
         * will still get added as 'Data.Foo'.
         */
        this.partAFields = [];
        this.eventQueue = [];
        this.config = config || {};
        if (parent) {
            this.telemetrySinks = parent.telemetrySinks;
            (_a = this.persistentDataFields).push.apply(_a, parent.persistentDataFields);
            this.config = __assign(__assign({}, parent.getConfig()), this.config);
            (_b = this.partAFields).push.apply(_b, parent.partAFields);
        }
        if (persistentDataFields) {
            (_c = this.persistentDataFields).push.apply(_c, persistentDataFields);
        }
    }
    SimpleTelemetryLogger.prototype.addEventContentType = function (dataFields, eventContentType) {
        dataFields.push(makeInt64DataField('EventContent.Type', eventContentType));
    };
    /**
     * SendTelemetryEvent is the basic way to send a telemetry event.
     * This should be used for sending telemetry about discrete events that happened
     * For telemetry that includes duration and/or success/failure, the Activity API may be more appropriate.
     * @param telemetryEvent - The telemetry event to send
     */
    SimpleTelemetryLogger.prototype.sendTelemetryEvent = function (telemetryEvent) {
        var clonedEvent = cloneEvent(telemetryEvent);
        var telemetryProperties = clonedEvent.telemetryProperties;
        // Nexus is no longer supported by OTelJS
        telemetryProperties.nexusTenantToken = SuppressNexus;
        // If either one is missing, we try to fill both
        var ariaTenantToken = telemetryProperties.ariaTenantToken;
        if (!ariaTenantToken) {
            var success = fillTenantTokens(clonedEvent.eventName, telemetryProperties);
            if (!success && !ariaTenantToken) {
                logNotification(0 /* LogLevel.Error */, 0 /* Category.Core */, function () { return 'No tenant token: ' + telemetryEvent.eventName; });
                return;
            }
        }
        this.sendTelemetryEventInternal(clonedEvent, 1 /* EventType.SendTelemetryEvent */);
    };
    /**
     * Sends a non-standard telemetry event like CustomerContentEvent or DirectNumericEvent
     * Note: This method should not be called directly. It is meant for when the SimpleTelemetryLogger is used as a passthrough.
     * @param telemetryEvent - The telemetry event to send
     * @param eventType - The type of event like CustomerContentEvent or DirectNumericEvent
     */
    SimpleTelemetryLogger.prototype.sendNonStandardEvent = function (telemetryEvent, eventType) {
        var clonedEvent = cloneEvent(telemetryEvent);
        this.sendTelemetryEventInternal(clonedEvent, eventType);
    };
    /**
     * Sends customer content to an appropriate compliant endpoint
     * @param event - The event to send
     * @param customerContentType - The type of customer content
     */
    SimpleTelemetryLogger.prototype.sendCustomerContent = function (customerContentEvent) {
        var clonedEvent = cloneEvent(customerContentEvent);
        var telemetryProperties = clonedEvent.telemetryProperties;
        var customerContentVersion = telemetryProperties.customerContentVersion;
        if (!customerContentVersion || customerContentVersion > CUSTOMER_CONTENT_VERSION) {
            telemetryProperties.customerContentVersion = CUSTOMER_CONTENT_VERSION;
        }
        // Suppress any tenant tokens that were passed in; the event must go to the customer content tenant.
        telemetryProperties.ariaTenantToken = undefined;
        telemetryProperties.nexusTenantToken = undefined;
        telemetryProperties.dnmToken = undefined;
        this.addEventContentType(clonedEvent.dataFields, 2 /* ContentType.CustomerContent */);
        return this.sendTelemetryEventInternal(clonedEvent, 2 /* EventType.CustomerContent */);
    };
    /**
     * Send a direct numerical measurement to the a DNM tenant
     * @param directNumericEvent - the event to send
     */
    SimpleTelemetryLogger.prototype.sendDirectNumericEvent = function (directNumericEvent) {
        var clonedEvent = cloneEvent(directNumericEvent);
        var telemetryProperties = clonedEvent.telemetryProperties;
        if (!telemetryProperties.dnmToken) {
            var success = fillTenantTokens(clonedEvent.eventName, telemetryProperties);
            if (!success && !telemetryProperties.dnmToken) {
                logNotification(0 /* LogLevel.Error */, 0 /* Category.Core */, function () { return 'No dnm token: ' + clonedEvent.eventName; });
                return;
            }
        }
        this.addEventContentType(clonedEvent.dataFields, 1 /* ContentType.AnonymousData */);
        this.sendTelemetryEventInternal(clonedEvent, 3 /* EventType.DirectNumericEvent */);
    };
    /**
     * Internal method to send a telemetry event
     * @param event - The telemetry event to send.
     * Note that it may be a CustomerContentEvent by virtue of having a customerContentVersion and customerContentType.
     */
    SimpleTelemetryLogger.prototype.sendTelemetryEventInternal = function (telemetryEvent, eventType) {
        if (eventType !== 2 /* EventType.CustomerContent */ &&
            (telemetryEvent.telemetryProperties.customerContentVersion || telemetryEvent.telemetryProperties.customerContentType)) {
            logNotification(0 /* LogLevel.Error */, 0 /* Category.Core */, function () { return 'Customer content'; });
            return;
        }
        try {
            if (this.telemetrySinks.length === 0) {
                if (this.config.enableQueue && this.eventQueue.length < MAX_EVENT_QUEUE_LENGTH) {
                    this.eventQueue.push([telemetryEvent, eventType]);
                }
                else {
                    logNotification(1 /* LogLevel.Warning */, 0 /* Category.Core */, function () { return 'No telemetry sinks are attached.'; });
                }
                return;
            }
            this.processTelemetryEvent(telemetryEvent, eventType);
        }
        catch (error) {
            logError(0 /* Category.Core */, 'SendTelemetryEvent', error);
            return;
        }
        this.telemetrySinks.forEach(function (telemetrySink) {
            try {
                if (eventType === 3 /* EventType.DirectNumericEvent */ || eventType === 2 /* EventType.CustomerContent */) {
                    if (telemetrySink.sendNonStandardEvent) {
                        telemetrySink.sendNonStandardEvent(telemetryEvent, eventType);
                    }
                }
                else {
                    telemetrySink.sendTelemetryEvent(telemetryEvent);
                }
            }
            catch (_e) {
                // Sinks should handle their own errors;
            }
        });
    };
    /**
     * Processes a telemetry event, but does not send it
     *   Adds persistent data fields
     *   Validates the event
     * Throws if the event is invalid
     * @param telemetryEvent - The telemetry event to process
     */
    SimpleTelemetryLogger.prototype.processTelemetryEvent = function (telemetryEvent, eventType) {
        var _a, _b;
        var _c;
        // The last-added data field wins, if the same-named data field is added more than once.
        // Persistent data fields should have lower priority than per-event data fields
        if (telemetryEvent.dataFields) {
            (_c = telemetryEvent.dataFields) === null || _c === void 0 ? void 0 : _c.unshift(makeStringDataField('OTelJS.Version', oteljsVersion));
            // Do not add logger persistent data fields to DNM events
            // All Part C fields should be explicitly added
            if (eventType !== 3 /* EventType.DirectNumericEvent */ && this.persistentDataFields) {
                (_a = telemetryEvent.dataFields).unshift.apply(_a, this.persistentDataFields);
            }
        }
        if (this.partAFields.length > 0) {
            var eventContract = telemetryEvent.eventContract || {
                name: '',
                dataFields: []
            };
            (_b = eventContract.dataFields).push.apply(_b, this.partAFields);
            telemetryEvent.eventContract = eventContract;
        }
        if (!this.config.disableValidation) {
            validateTelemetryEvent(telemetryEvent);
        }
    };
    /**
     * Adds a telemetry sink, to which events will be sent
     * @param sink - The telemetry sink to add
     */
    SimpleTelemetryLogger.prototype.addSink = function (sink) {
        this.telemetrySinks.push(sink);
        this.flushQueue();
    };
    /**
     * Flush the queue of telemetry events that were accumulated before a sink was attached.
     * The queue is emptied whether or not a sink is attached.
     *
     * Note that all events are sent at once. If an event throttler is enabled on a telemetry sink,
     * it may be triggered; you may choose to only connect the event throttler after flushing the queue.
     */
    SimpleTelemetryLogger.prototype.flushQueue = function () {
        var eventQueue = this.eventQueue;
        this.eventQueue = [];
        if (this.telemetrySinks.length > 0) {
            for (var _i = 0, eventQueue_1 = eventQueue; _i < eventQueue_1.length; _i++) {
                var _a = eventQueue_1[_i], telemetryEvent = _a[0], eventType = _a[1];
                this.sendTelemetryEventInternal(telemetryEvent, eventType);
            }
        }
    };
    /**
     * Sets a tenant token for a single namespace. Events within the namespace will be sent to the corresponding tenant.
     * @param namespace - The namespace to which the tokens apply, such as Office.System.
     * @param ariaTenantToken - The Aria tenant token
     * @param nexusTenantToken - The Nexus tenant token
     */
    SimpleTelemetryLogger.prototype.setTenantToken = function (namespace, ariaTenantToken, nexusTenantToken) {
        setTenantTokens(namespace, { ariaTenantToken: ariaTenantToken, nexusTenantToken: nexusTenantToken });
    };
    /**
     * Sets a tenant token for a single namespace for DNM. Events within the namespace will be sent to the corresponding tenant.
     * @param namespace - The namespace to which the tokens apply, such as Office.System.
     * @param dnmToken - The Aria tenant token for direct numerical measurement events
     */
    SimpleTelemetryLogger.prototype.setDNMToken = function (namespace, dnmToken) {
        setTenantTokens(namespace, { dnmToken: dnmToken });
    };
    /**
     * Clone a telemetry event; generally so we don't modify the input telemetry event
     * Also sets dataFields to [] if it isn't already set
     * @param telemetryEvent - The event to clone
     */
    SimpleTelemetryLogger.prototype.cloneEvent = function (telemetryEvent) {
        return cloneEvent(telemetryEvent);
    };
    /**
     * Gets the currently applied configuration
     */
    SimpleTelemetryLogger.prototype.getConfig = function () {
        return this.config;
    };
    return SimpleTelemetryLogger;
}());
export { SimpleTelemetryLogger };
//# sourceMappingURL=SimpleTelemetryLogger.js.map